import type { StudEndHit, StudEndTrimContext } from "building-types/framing/wallFramingStudEndTrimTypes";
import { WallFramingType } from "building-types/framing/wallFramingTypes";
import { DIST_EPSILON } from "geom/core";
import { line2Normal } from "geom/line2";
import { planeByNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2ByTwoPoints, polyLine2Edge, polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { classifyBoundaryEdgeType, edgeIndicesAtVertex } from "./wallFramingUtils";

/** End hits for a vertical boundary stud on perimeter edge `studEdgeIndex`. */
export function boundaryStudEndHits(
  perimeter: PolyLine2,
  studEdgeIndex: number,
): { readonly startHit: StudEndHit; readonly endHit: StudEndHit } {
  const edgeCount = polyLine2EdgeCount(perimeter);
  const edge = polyLine2Edge(perimeter, studEdgeIndex);
  const prevEdgeIndex = (studEdgeIndex - 1 + edgeCount) % edgeCount;
  const nextEdgeIndex = (studEdgeIndex + 1) % edgeCount;
  return {
    startHit: { edgeIndex: prevEdgeIndex, x: edge[0], y: edge[1] },
    endHit: { edgeIndex: nextEdgeIndex, x: edge[2], y: edge[3] },
  };
}

export function isEdgePerpendicularToStud(edge: PolyLine2, studDir: Vec2): boolean {
  const edx = edge[2] - edge[0];
  const edy = edge[3] - edge[1];
  const edgeLen = Math.hypot(edx, edy);
  const studLen = Math.hypot(studDir[0], studDir[1]);
  if (edgeLen < DIST_EPSILON || studLen < DIST_EPSILON) {
    return true;
  }
  const dot = Math.abs((edx * studDir[0] + edy * studDir[1]) / (edgeLen * studLen));
  return dot < DIST_EPSILON;
}

function studDirectionFromSegment(segment: PolyLine2): Vec2 {
  const dx = segment[2] - segment[0];
  const dy = segment[3] - segment[1];
  const len = Math.hypot(dx, dy);
  if (len < DIST_EPSILON) {
    return [0, 1];
  }
  return [dx / len, dy / len];
}

function plateStackCountForEdge(ctx: StudEndTrimContext, edgeType: WallFramingType): number {
  if (edgeType === WallFramingType.BOTTOM_PLATE) {
    return ctx.bottomPlateCount;
  }
  if (edgeType === WallFramingType.TOP_PLATE) {
    return ctx.topPlateCount;
  }
  return 0;
}

function perpendicularTrimOffset(stackCount: number, plateThickness: number): number {
  if (stackCount <= 0) {
    return 0;
  }
  return stackCount * plateThickness;
}

/**
 * Shorten a stud segment at plate ends where the plate edge is perpendicular to the stud run.
 * Sloped plate ends are left to clip planes in 3D.
 */
export function trimStudSegmentAtPerpendicularPlateEnds(
  segment: PolyLine2,
  startHit: StudEndHit | undefined,
  endHit: StudEndHit | undefined,
  ctx: StudEndTrimContext,
): PolyLine2 {
  const studDir = studDirectionFromSegment(segment);
  let x0 = segment[0];
  let y0 = segment[1];
  let x1 = segment[2];
  let y1 = segment[3];

  if (startHit) {
    const edge = polyLine2Edge(ctx.perimeter.polyline, startHit.edgeIndex);
    const edgeType = classifyBoundaryEdgeType(ctx.perimeter.polyline, startHit.edgeIndex);
    if (
      isEdgePerpendicularToStud(edge, studDir) &&
      (edgeType === WallFramingType.BOTTOM_PLATE || edgeType === WallFramingType.TOP_PLATE)
    ) {
      const offset = perpendicularTrimOffset(plateStackCountForEdge(ctx, edgeType), ctx.profileDescriptor.height);
      if (offset > DIST_EPSILON) {
        x0 += studDir[0] * offset;
        y0 += studDir[1] * offset;
      }
    }
  }

  if (endHit) {
    const edge = polyLine2Edge(ctx.perimeter.polyline, endHit.edgeIndex);
    const edgeType = classifyBoundaryEdgeType(ctx.perimeter.polyline, endHit.edgeIndex);
    if (
      isEdgePerpendicularToStud(edge, studDir) &&
      (edgeType === WallFramingType.BOTTOM_PLATE || edgeType === WallFramingType.TOP_PLATE)
    ) {
      const offset = perpendicularTrimOffset(plateStackCountForEdge(ctx, edgeType), ctx.profileDescriptor.height);
      if (offset > DIST_EPSILON) {
        x1 -= studDir[0] * offset;
        y1 -= studDir[1] * offset;
      }
    }
  }

  return polyLine2ByTwoPoints(x0, y0, x1, y1);
}

function slopedPlateClipPlaneAtHit(hit: StudEndHit, ctx: StudEndTrimContext, studDir: Vec2): Plane | null {
  const { perimeter, profileDescriptor, elevation } = ctx;
  const edgeType = classifyBoundaryEdgeType(perimeter.polyline, hit.edgeIndex);
  if (edgeType !== WallFramingType.BOTTOM_PLATE && edgeType !== WallFramingType.TOP_PLATE) {
    return null;
  }

  const edge = polyLine2Edge(perimeter.polyline, hit.edgeIndex);
  if (isEdgePerpendicularToStud(edge, studDir)) {
    return null;
  }

  const stackCount = plateStackCountForEdge(ctx, edgeType);
  if (stackCount <= 0) {
    return null;
  }

  const [inX, inY] = line2Normal(edge[0], edge[1], edge[2], edge[3], true);
  const offset = stackCount * profileDescriptor.height;
  const originX = hit.x + inX * offset;
  const originLayoutY = hit.y + inY * offset;
  const originZ = originLayoutY + elevation;

  // clipFacesWithPlanes keeps the BACK half-space (see clippingNodeNew(..., true)). The normal must
  // point away from the stud cavity so BACK retains material on the stud side of the plate face.
  const nx = -inX;
  const ny = 0;
  const nz = -inY;

  return planeByNormal(originX, ny, originZ, nx, ny, nz);
}

/**
 * Clip planes for sloped plate ends. At a peak vertex shared by two sloped top plates, returns
 * one plane per incident sloped plate edge so the stud sits below both plate stacks.
 */
export function buildStudEndClipPlanes(
  segment: PolyLine2,
  startHit: StudEndHit | undefined,
  endHit: StudEndHit | undefined,
  ctx: StudEndTrimContext,
): Plane[] {
  const studDir = studDirectionFromSegment(segment);
  const planes: Plane[] = [];

  const addPlanesForHit = (hit: StudEndHit | undefined): void => {
    if (!hit) {
      return;
    }

    const vertexEdges = edgeIndicesAtVertex(ctx.perimeter.polyline, hit.x, hit.y);
    const edgeCandidates = vertexEdges.length >= 2 ? vertexEdges : [hit.edgeIndex];

    for (const edgeIndex of edgeCandidates) {
      const candidateHit: StudEndHit = { edgeIndex, x: hit.x, y: hit.y };
      const plane = slopedPlateClipPlaneAtHit(candidateHit, ctx, studDir);
      if (plane) {
        planes.push(plane);
      }
    }
  };

  addPlanesForHit(startHit);
  addPlanesForHit(endHit);

  return planes;
}
